import { useWorkspaceTab } from '@components/Common/EntityWorkspace/useWorkspaceTab';
import { canSeeRevenue, isSuperAdmin, useCurrentUserId, useCurrentUserRole } from '@components/Common/Utils/roles';
import ConfirmDialog from '@components/Common/ConfirmDialog';
import { currentUserDetails } from '@store/accessor';
import SkeletonLoader from '@components/Common/Skeleton/SkeletonLoader';
import { useActivityRail } from '@components/Common/EntityWorkspace/useActivityRail';
import CRMStatusChip from '@components/Common/CRMStatusChip';
import {
  ActivityRail,
  EntityWorkspace,
  IdentityRail,
  SectionCard,
  WorkspaceField,
} from '@components/Common/EntityWorkspace';
import RecordActivityCard from '@components/Common/EntityWorkspace/RecordActivityCard';
import type { GlanceRow, TimelineEntry, WorkspaceTab } from '@components/Common/EntityWorkspace';
import ErrorMessage from '@components/Common/ErrorMessage';
import NotesSection from '@components/Common/NotesSection';
import { matchColor, unscoredReason } from '@components/Common/MatchPercent';
import RecordLink from '@components/Common/RecordLink';
import FieldLabel from '@components/Common/FieldLabel';
import FormField from '@components/Common/FormField';
import WorkspaceInput from '@components/Common/WorkspaceInput';
import { useAuthorNames } from '@components/Common/NotesSection/useAuthorNames';
import { BEFORE_SUBMISSION, isHired, resolveStatus, statusSummary } from '@constants/applicationStatus';
import { useHrUsers } from '@components/Common/Utils/useHrUsers';
import {
  overrideRightToRepresentMutation,
  requestRightToRepresentMutation,
  jobApplicationFeedbackSave,
  jobApplicationInternalAttributesSave,
  jobApplicationOfferedSalarySave,
  jobApplicationNoteSave,
  jobApplicationTracking,
} from '@constants/graphQLQueries';
import {
  DELETED_CANDIDATE_LABEL,
  candidateDisplayName,
  isCandidateRecordDeleted,
} from '@helpers/candidateRecord';
import { fetchGraphQLApiData } from '@helpers/graphQLApi';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import HistoryIcon from '@mui/icons-material/History';
import PersonOutlineIcon from '@mui/icons-material/PersonOutlined';
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined';
import {
  Box,
  Button,
  IconButton,
  Rating,
  Tooltip,
} from '@mui/material';
import { resolveResumeUrl } from '@components/Common/resumeUrl';
import { useToast } from '@components/Common/Toast/ToastProvider';
import PlacementFeeCard from './PlacementFeeCard';
import StatusCard from './StatusCard';
import NextActionCard from './NextActionCard';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { htmlToText } from '@helpers/utils';
import './index.scss';
import { formatDate } from '@helpers/date';

type NoteVisibility = 'INTERNAL' | 'SHARED';

/**
 * Provenance, spelled out.
 *
 * The generic formatter would render HR_ON_BEHALF as "Hr_on_behalf", and the raw enum is not what
 * a recruiter should have to read to learn where a candidate came from. An unrecognised value
 * falls through to "Applied directly", which is both the common case and the safer guess: telling
 * an operator we sourced someone we did not is the mistake with consequences.
 */
const SOURCE_LABELS: Record<string, string> = {
  SELF_APPLIED: 'Applied directly',
  HR_ON_BEHALF: 'Submitted by us',
};

export function sourceLabel(value?: string | null): string {
  return SOURCE_LABELS[(value ?? '').trim().toUpperCase()] ?? SOURCE_LABELS.SELF_APPLIED;
}

interface Application {
  id: string;
  status: string;
  subStatus?: string | null;
  closedReason?: string | null;
  closedNote?: string | null;
  ownerId?: string | null;
  nextAction?: string | null;
  nextActionAt?: string | null;
  employerVisible?: boolean | null;
  rtrRequestedAt?: string | null;
  rtrConfirmedAt?: string | null;
  rtrRevokedAt?: string | null;
  rtrOverriddenAt?: string | null;
  jobSeekerId?: string;
  jobId?: string;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: string;
  updatedBy?: string;
  /** SELF_APPLIED or HR_ON_BEHALF; resolved server-side, so never null on a row the API returned. */
  source?: string;
  /** 0 to 100; null until both the candidate and the job have a profile vector. */
  relevancyPercent?: number | null;
  relevancyStatus?: string | null;
  jobSeeker?: {
    personalDetails?: {
      fullName?: string;
      email?: string;
      phone?: string;
      city?: string;
      state?: string;
      country?: string;
    };
    jobPreferences?: {
      preferredCities?: string[];
      noticePeriod?: string;
      /** One value in the schema (`JobPreferences.jobType: String`), not a list. */
      jobType?: string;
      preferredRoles?: string[];
    };
    resumeUrl?: string;
    internalAttributes?: Array<{ key: string; value: string }>;
  };
  job?: { title?: string; companyName?: string; companyId?: string; location?: string;
          hiringMode?: string; assignedHrId?: string | null;
          requirementStatus?: string | null; jobStatus?: string | null };
  history?: Array<{
    status: string;
    previousStatus?: string | null;
    subStatus?: string | null;
    closedReason?: string | null;
    note?: string | null;
    date: string;
    changedBy: string;
  }>;
  notes?: Array<{ changedBy: string; note: string; date: string; visibility?: string }>;
  feedback?: Array<{ rating?: number | null; comment?: string; date?: string; authorId?: string }>;
  internalAttributes?: Array<{ key: string; value: string }>;
  offeredSalary?: number | null;
  offeredSalaryCurrency?: string;
}

const TABS: WorkspaceTab[] = [
  { id: 'overview', label: 'Overview', icon: <FactCheckOutlinedIcon /> },
  { id: 'candidate', label: 'Candidate', icon: <PersonOutlineIcon /> },
  { id: 'interviews', label: 'Interviews', icon: <EventOutlinedIcon /> },
  { id: 'documents', label: 'Documents', icon: <DescriptionOutlinedIcon /> },
  { id: 'history', label: 'History', icon: <HistoryIcon /> },
];

const initialsOf = (name?: string) =>
  (name || '')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join('') || 'A';

const fmt = (d?: string) => formatDate(d);

/**
 * HR-only custom attributes on a candidate (e.g. Religion). Free-form key/value rows that
 * the backend strips for employer/jobseeker callers, so the candidate never sees them.
 */
const InternalFieldsCard: React.FC<{
  attributes?: Array<{ key: string; value: string }>;
  onSave: (attrs: { key: string; value: string }[]) => void | Promise<void>;
}> = ({ attributes, onSave }) => {
  const [rows, setRows] = useState<Array<{ key: string; value: string }>>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setRows((attributes ?? []).map((a) => ({ key: a.key ?? '', value: a.value ?? '' })));
  }, [attributes]);

  const update = (i: number, field: 'key' | 'value', v: string) =>
    setRows((r) => r.map((row, idx) => (idx === i ? { ...row, [field]: v } : row)));
  const save = async () => {
    setSaving(true);
    try {
      await onSave(rows.filter((r) => r.key.trim()));
    } finally {
      setSaving(false);
    }
  };

  return (
    <SectionCard
      title="Internal fields"
      icon={<BadgeOutlinedIcon />}
      meta="HR only — hidden from the candidate"
    >
      <Box sx={{ gridColumn: '1 / -1', display: 'flex', flexDirection: 'column', gap: 1 }}>
        {rows.length === 0 && (
          <div style={{ fontSize: 'var(--font-size-2xs)', color: 'var(--muted)' }}>
            No internal fields yet. Add custom attributes like Religion, Community, or
            sourcing notes — only HRs and Super Admins can see these.
          </div>
        )}
        {rows.map((row, i) => (
          <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <WorkspaceInput
              flex
              placeholder="Field (e.g. Religion)"
              value={row.key}
              onChange={(v) => update(i, 'key', v)}
            />
            <WorkspaceInput
              flex
              placeholder="Value"
              value={row.value}
              onChange={(v) => update(i, 'value', v)}
            />
            <IconButton
              size="small"
              onClick={() => setRows((r) => r.filter((_, idx) => idx !== i))}
              sx={{ color: 'var(--red)' }}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Box>
        ))}
        <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
          <Button size="small" onClick={() => setRows((r) => [...r, { key: '', value: '' }])}>
            + Add field
          </Button>
          <Button size="small" variant="contained" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save internal fields'}
          </Button>
        </Box>
      </Box>
    </SectionCard>
  );
};

/** Salary offered to the candidate for this role (Excel "salary offered"). */
const OfferedSalaryCard: React.FC<{
  amount?: number | null;
  currency?: string;
  onSave: (amount: number | null) => void | Promise<void>;
}> = ({ amount, currency, onSave }) => {
  const [value, setValue] = useState<string>('');
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setValue(amount == null ? '' : String(amount));
  }, [amount]);
  const save = async () => {
    setSaving(true);
    try {
      const n = value.trim() === '' ? null : Number(value);
      await onSave(n != null && Number.isNaN(n) ? null : n);
    } finally {
      setSaving(false);
    }
  };
  return (
    <SectionCard title="Offer" icon={<BadgeOutlinedIcon />} meta="Recruiting ops">
      {/* The shared field-row, not a hand-rolled flex: this was `alignItems: 'center'` in an sx,
          which centred Save against the label *and* the input and left it 13.6px above the input's
          centre line. Written in the prop, no stylesheet could have corrected it. */}
      <Box className="cc-field-row cc-field--full">
        <Box className="cc-field-row__grow">
          <FormField
            required={false}
            name="offered-salary"
            type="number"
            label={`Offered salary (${currency || 'INR'})`}
            value={value}
            onChange={(_n, v) => setValue(String(v))}
          />
        </Box>
        <Button size="small" variant="contained" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </Box>
    </SectionCard>
  );
};

const ApplicationWorkspace: React.FC = () => {
  const { applicationId } = useParams<{ applicationId: string }>();
  const navigate = useNavigate();
  const [app, setApp] = useState<Application | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [overriding, setOverriding] = useState(false);
  const [resumeOpening, setResumeOpening] = useState(false);
  const { showToast } = useToast();
  const role = useCurrentUserRole();
  const currentUserId = useCurrentUserId();
  /*
   * Managed Hiring: a client's pipeline belongs to the recruiter who owns it.
   *
   * Everyone internal can READ this page -- covering for a colleague, or picking a client up
   * mid-cycle, needs the full pipeline. What is owned is the right to MOVE it, because two
   * recruiters advancing the same candidate is how a client gets told two different things.
   *
   * Mirrors guardManagedStatusChange on the backend, which is the authority; this only spares
   * the user a refusal they cannot act on. A MANAGED job with no assigned recruiter stays open,
   * or nobody could work it at all.
   */
  // The candidate's recruiter: their own owner, else the job's. The backend is the authority
  // (ApplicationWorkflow); this spares the user a refusal they cannot act on.
  const managed = app?.job?.hiringMode === 'MANAGED';
  const recordOwner = managed ? (app?.ownerId || app?.job?.assignedHrId || '') : '';
  const canChangeStatus =
    isSuperAdmin(role) || !recordOwner || recordOwner === currentUserId;
  const canOverride = isSuperAdmin(role);
  /*
   * The consent gate: a recruiter's candidate on a managed job, not yet put to the client.
   * `consentOpen` is before submission with no consent on record; asked is whether a request is
   * live (a declined one is not).
   */
  const stageNow = resolveStatus(app?.status).stage?.label ?? '';
  const consentOpen = managed
    && app?.source === 'HR_ON_BEHALF'
    && BEFORE_SUBMISSION.includes(stageNow)
    && !app?.rtrConfirmedAt
    && !app?.rtrOverriddenAt;
  const consentAsked = !!app?.rtrRequestedAt && !app?.rtrRevokedAt;
  const awaitingConsent = consentOpen && consentAsked;
  const [asking, setAsking] = useState(false);
  const hrUsers = useHrUsers(!!app);
  /* In the URL, not in state: a tab is a place, and this one was unshareable and did
     not survive a reload. See useWorkspaceTab. */
  const [activeTab, setActiveTab] = useWorkspaceTab(TABS, 'overview');
  const [activityOpen, toggleActivity] = useActivityRail();

  // Feedback + delete state
  const [rating, setRating] = useState<number | null>(null);
  const [comment, setComment] = useState('');
  const [savingFb, setSavingFb] = useState(false);
  const [delOpen, setDelOpen] = useState(false);
  const [confirmTitle, setConfirmTitle] = useState('');


  const load = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await fetchGraphQLApiData(jobApplicationTracking(id));
      const record = data?.getJobApplicationById;
      if (!record) {
        setError('Job application not found.');
        return;
      }
      /*
       * `_id` is what GraphQL returns; `id` is what this component reads. Every mutation on this
       * page interpolates `app.id`, so storing the record raw made all of them address the string
       * "undefined": status, notes, offered salary, internal attributes, feedback and delete alike.
       * The status control was where it showed, because that is the one people use.
       *
       * Nothing caught it. `fetchGraphQLApiData` returns `any`, so `Application.id` is a claim
       * TypeScript accepts without checking, and the mutation "succeeds" — the backend's findById
       * simply matches nothing and returns null, with no GraphQL error to throw on.
       *
       * Every sibling workspace already does this mapping: JobSeekerWorkspace:101,
       * EmployerWorkspace:55, JobWorkspace:91. This one was the exception.
       */
      setApp({ ...record, id: record._id ?? '' });
    } catch {
      setError('Failed to load the application. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (applicationId) load(applicationId);
  }, [applicationId, load]);

  // ── Actions ──────────────────────────────────────────────────────────
  /**
   * Submit a candidate who agreed somewhere this system cannot see.
   *
   * The gate is real — the backend refuses to move an application out of "Awaiting consent"
   * without a recorded consent — so a candidate who said yes on a call would otherwise be stuck.
   * This is the sanctioned exception, and it is attributed: an override that cannot be traced to a
   * person is not an audit trail.
   *
   * Offered only to a Super Admin, and refused by the backend for anyone else. The UI decides what
   * is shown; the server decides what is allowed.
   */
  const overrideConsent = async () => {
    if (!app) return;
    setOverriding(true);
    try {
      await fetchGraphQLApiData(
        overrideRightToRepresentMutation(app.id, currentUserDetails().email),
        { showStatus: true, message: 'Override recorded' },
      );
      setOverrideOpen(false);
      await load(app.id);
    } catch (e: any) {
      setError(
        e?.message?.includes('already confirmed')
          ? 'This candidate has already confirmed, so no override is needed.'
          : 'Could not override the consent gate. Please try again.',
      );
    } finally {
      setOverriding(false);
    }
  };

  const addNote = async (html: string, visibility?: NoteVisibility) => {
    if (!app) return;
    try {
      await fetchGraphQLApiData(jobApplicationNoteSave(app.id, html, visibility));
      await load(app.id);
    } catch {
      setError('Failed to add the note. Please try again.');
    }
  };

  const saveOfferedSalary = async (amount: number | null) => {
    if (!app) return;
    try {
      await fetchGraphQLApiData(
        jobApplicationOfferedSalarySave(app.id, amount, app.offeredSalaryCurrency || 'INR'),
        { showStatus: true, message: 'Offered salary saved' },
      );
      await load(app.id);
    } catch {
      setError('Failed to save the offered salary. Please try again.');
    }
  };

  const saveInternalAttributes = async (attrs: { key: string; value: string }[]) => {
    if (!app) return;
    try {
      await fetchGraphQLApiData(
        jobApplicationInternalAttributesSave(app.id, attrs),
        { showStatus: true, message: 'Internal fields saved' },
      );
      await load(app.id);
    } catch {
      setError('Failed to save internal fields. Please try again.');
    }
  };

  const addFeedback = async () => {
    if (!app || (rating == null && !comment.trim())) return;
    setSavingFb(true);
    try {
      await fetchGraphQLApiData(
        jobApplicationFeedbackSave(app.id, rating ?? null, comment.trim()),
        { showStatus: true, message: 'Feedback saved' },
      );
      setRating(null);
      setComment('');
      await load(app.id);
    } catch {
      setError('Failed to save feedback. Please try again.');
    } finally {
      setSavingFb(false);
    }
  };

  const doDelete = async () => {
    if (!app) return;
    if (confirmTitle.trim().toLowerCase() !== (app.job?.title ?? '').trim().toLowerCase()) return;
    try {
      await fetchGraphQLApiData(
        `mutation { deleteJobApplicationById(_id: "${app.id}") { _id } }`,
        { showStatus: true, message: 'Job application deleted' },
      );
      setDelOpen(false);
      navigate('/applications/list');
    } catch {
      setError('Failed to delete the application. Please try again.');
    }
  };

  // Shown as a toast: `error` only renders while there is no record, so a refusal set there after
  // the page has loaded would never be seen.
  const showError = useCallback((m: string) => showToast(m, 'error'), [showToast]);

  const askConsent = async () => {
    if (!app) return;
    setAsking(true);
    try {
      await fetchGraphQLApiData(requestRightToRepresentMutation(app.id), {
        showStatus: true,
        message: 'Consent requested from the candidate',
      });
      await load(app.id);
    } catch (e: any) {
      showToast(String(e?.message ?? '').replace(/^GraphQL errors:\s*/, '') || 'Could not ask for consent.', 'error');
    } finally {
      setAsking(false);
    }
  };

  const reload = useCallback(async () => {
    if (applicationId) await load(applicationId);
  }, [applicationId, load]);

  const stageCard = app ? (
    <StatusCard
      app={app}
      canMove={canChangeStatus}
      superAdmin={isSuperAdmin(role)}
      consentPending={consentOpen}
      onChanged={reload}
      onError={showError}
      consentAction={(
        <>
          {/* Asking moves them to Qualified and emails them; their yes submits them. */}
          {consentOpen && !consentAsked && canChangeStatus && (
            <Button size="small" variant="outlined" disabled={asking} onClick={askConsent}>
              {asking ? 'Asking…' : 'Ask for consent'}
            </Button>
          )}
          {awaitingConsent && canOverride && (
            <Button size="small" variant="outlined" onClick={() => setOverrideOpen(true)}>
              Submit without consent
            </Button>
          )}
        </>
      )}
    />
  ) : null;

  // changedBy holds an ID, so anything rendering it raw shows a 24-character
  // ObjectId where a person's name belongs.
  const resolveAuthor = useAuthorNames([
    ...(app?.history ?? []).map((h) => h.changedBy),
    ...(app?.notes ?? []).map((n) => n.changedBy),
  ]);

  const timeline: TimelineEntry[] = useMemo(() => {
    if (!app) return [];
    const fromHistory: TimelineEntry[] = (app.history ?? []).map((h, i) => ({
      id: `h-${i}`,
      kind: 'status',
      text: (
        <>
          {h.previousStatus ? <>{resolveStatus(h.previousStatus).stage?.label ?? h.previousStatus} to </> : 'Moved to '}
          <strong>{statusSummary(h.status, h.subStatus, h.closedReason)}</strong>
        </>
      ),
      body: h.note || undefined,
      meta: `${fmt(h.date)} · ${h.changedBy ? resolveAuthor(h.changedBy) : 'System'}`,
    }));
    const fromNotes: TimelineEntry[] = (app.notes ?? []).map((n, i) => ({
      id: `n-${i}`,
      kind: 'note',
      text: 'Note added',
      body: htmlToText(n.note),
      meta: `${fmt(n.date)} · ${resolveAuthor(n.changedBy)}`,
    }));
    return [...fromHistory, ...fromNotes].sort((a, b) =>
      String(b.meta).localeCompare(String(a.meta)),
    );
  }, [app, resolveAuthor]);

  if (loading && !app) return <SkeletonLoader variant="workspace" />;
  if (error && !app) return <ErrorMessage page message={error} />;
  if (!app) return null;

  const pd = app.jobSeeker?.personalDetails ?? {};
  const jp = app.jobSeeker?.jobPreferences ?? {};
  /*
   * The candidate was hard-deleted and this application outlived them. Nothing here can be
   * recovered, so the page says so plainly instead of rendering a nameless record that reads as a
   * page that failed to load. What survives is still worth showing: the notes, the status history
   * and the job it was for.
   */
  const candidateDeleted = isCandidateRecordDeleted(app);

  /*
   * The score arrives with the record. It used to be a second call that turned 0 into "-", and 0
   * was what came back for every candidate without a profile vector -- most new applicants, since
   * the CV extraction that builds one was only ever run by hand. Null now says "not scored", and
   * the backend queues that extraction on apply, so a CV on file means a score is on its way.
   */
  const relevancy = app.relevancyPercent ?? null;
  const unscored = unscoredReason(app.relevancyStatus, !!app.jobSeeker?.resumeUrl);
  const relevancyText = relevancy != null
    ? `${Math.round(relevancy)}%`
    : <Tooltip title={unscored.hint}><span>{unscored.label}</span></Tooltip>;

  // An application is the join between a candidate and a job, so both have to
  // be one click away. The popup this replaced linked all three; the workspace
  // offered only "back to list", which made it a dead end.
  const glance: GlanceRow[] = [
    {
      key: 'Role',
      value: app.jobId && app.job?.title
        ? <RecordLink to={`/jobs/${app.jobId}`} label="Open the job">{app.job.title}</RecordLink>
        : app.job?.title ?? '-',
    },
    {
      key: 'Company',
      value: app.job?.companyId && app.job?.companyName
        ? <RecordLink to={`/companies/${app.job.companyId}`} label="Open the company">{app.job.companyName}</RecordLink>
        : app.job?.companyName ?? '-',
    },
    { key: 'Applied', value: fmt(app.createdAt), mono: true },
    // Whether we put this candidate forward or they came to us. It was previously inferable only
    // from createdBy reading "SYSTEM" in the audit block, which says which process wrote the row
    // rather than who decided to apply, and which nobody would think to read that way.
    { key: 'Source', value: sourceLabel(app.source) },
    {
      key: 'AI match',
      value: relevancyText,
      ...(relevancy != null ? { valueColor: matchColor(relevancy) } : {}),
    },
  ];

  const identityRail = (
    <IdentityRail
      initials={candidateDeleted ? '--' : initialsOf(pd.fullName)}
      name={candidateDisplayName(app, 'Candidate')}
      subId={app.id}
      statusChip={<CRMStatusChip status={app.status} kind="application" />}
      secondaryActions={
        <Tooltip title="Delete application">
          <IconButton
            size="small"
            onClick={() => { setConfirmTitle(''); setDelOpen(true); }}
            sx={{ border: '1px solid var(--line)', borderRadius: '8px', color: 'var(--red)' }}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      }
      glance={glance}
      audit={{
        createdAt: app.createdAt,
        createdBy: app.createdBy,
        updatedAt: app.updatedAt,
        updatedBy: app.updatedBy,
      }}
    />
  );

  const feedbackSection = (
    <SectionCard title="Feedback" icon={<RateReviewOutlinedIcon />} meta="Recruiting ops">
      {(app.feedback ?? []).length > 0 && (
        <Box sx={{ gridColumn: '1 / -1', display: 'flex', flexDirection: 'column', gap: 1, mb: 1 }}>
          {(app.feedback ?? []).map((f, i) => (
            <WorkspaceField
              key={i}
              label={`${f.rating ? `★ ${f.rating}/5` : 'Note'}${f.date ? ` · ${fmt(f.date)}` : ''}`}
              value={f.comment || '-'}
              full
            />
          ))}
        </Box>
      )}
      <Box sx={{ gridColumn: '1 / -1', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {/* Stacked like a field, not label-beside-control.
            This used .cc-field__label, a 10px uppercase micro-label with 0.11em tracking, sitting
            inline next to the stars. The comment box below it carries a normal 13px sentence-case
            FormField label, so one card showed two different label specs. FieldLabel is the
            canonical one, and it is designed to sit above its control, which is why the row
            becomes a column rather than keeping the label inline.
            The name is carried by role="group" + aria-labelledby rather than by htmlFor: Rating
            renders a set of radios inside a span, and a label pointing at a span names nothing. */}
        <Box role="group" aria-labelledby="feedback-rating-label">
          <FieldLabel id="feedback-rating-label">Rating</FieldLabel>
          <Rating value={rating} onChange={(_, v) => setRating(v)} size="small" />
        </Box>
        {/* FormField, not a bare TextField. The border and focus ring were already correct from
            theme.ts; what was missing is the multiline padding reset FormField applies, without
            which the root keeps MUI's 8.5px/14px on top of the theme's own inset and the text sits
            about 14px further in than every field above it. */}
        <FormField
          required={false}
          name="feedback-comment"
          label="Comment"
          value={comment}
          onChange={(_, v) => setComment(String(v))}
          placeholder="Add feedback comment"
          multiline
          rows={2}
        />
        <Box>
          <Button
            variant="contained"
            size="small"
            onClick={addFeedback}
            disabled={savingFb || (rating == null && !comment.trim())}
          >
            {savingFb ? 'Saving…' : 'Save feedback'}
          </Button>
        </Box>
      </Box>
    </SectionCard>
  );

  const overview = (
    <>
      <SectionCard title="Application details" icon={<FactCheckOutlinedIcon />} meta="6 fields">
        <WorkspaceField
          label="Candidate"
          value={candidateDeleted
            ? DELETED_CANDIDATE_LABEL
            : app.jobSeekerId && pd.fullName
              ? <RecordLink to={`/jobseekers/${app.jobSeekerId}`} label="Open the candidate">{pd.fullName}</RecordLink>
              : pd.fullName}
        />
        <WorkspaceField
          label="Role applied"
          value={app.jobId && app.job?.title
            ? <RecordLink to={`/jobs/${app.jobId}`} label="Open the job">{app.job.title}</RecordLink>
            : app.job?.title}
        />
        <WorkspaceField
          label="Company"
          value={app.job?.companyId && app.job?.companyName
            ? <RecordLink to={`/companies/${app.job.companyId}`} label="Open the company">{app.job.companyName}</RecordLink>
            : app.job?.companyName}
        />
        <WorkspaceField label="Applied" value={fmt(app.createdAt)} mono />
        <WorkspaceField label="Status" value={statusSummary(app.status, app.subStatus, app.closedReason)} />
        <WorkspaceField
          label="AI match"
          value={relevancyText}
          valueColor={relevancy != null ? matchColor(relevancy) : undefined}
        />
      </SectionCard>

      <OfferedSalaryCard
        amount={app.offeredSalary}
        currency={app.offeredSalaryCurrency}
        onSave={saveOfferedSalary}
      />

      <NextActionCard
        applicationId={app.id}
        ownerId={app.ownerId}
        nextAction={app.nextAction}
        nextActionAt={app.nextActionAt}
        hrUsers={hrUsers}
        canEdit={canChangeStatus}
        canChangeOwner={isSuperAdmin(role)}
        onChanged={reload}
        onError={showError}
      />

      {/* Once hired (an offer made, or joined): the fee this placement earned. */}
      {/* Not for Marketing or an HR Lead: placement fees are money, refused to both on the proxy. */}
      {isHired(app.status, app.subStatus) && canSeeRevenue(role) && (
        <PlacementFeeCard
          applicationId={app.id}
          canEdit={isSuperAdmin(role)}
          offeredSalary={app.offeredSalary}
          offeredCurrency={app.offeredSalaryCurrency}
        />
      )}

      {(app.jobSeeker?.internalAttributes?.length ?? 0) > 0 && (
        <SectionCard
          title="Candidate internal fields"
          icon={<BadgeOutlinedIcon />}
          meta="From the candidate profile — HR only"
        >
          {(app.jobSeeker?.internalAttributes ?? []).map((a, i) => (
            <WorkspaceField key={i} label={a.key} value={a.value || '-'} />
          ))}
        </SectionCard>
      )}

      <InternalFieldsCard
        attributes={app.internalAttributes}
        onSave={saveInternalAttributes}
      />

      <SectionCard title="Screening summary" icon={<BadgeOutlinedIcon />} meta="4 fields">
        <WorkspaceField label="Notice period" value={jp.noticePeriod} />
        <WorkspaceField label="Preferred location" value={(jp.preferredCities ?? []).join(', ') || '-'} />
        <WorkspaceField label="Preferred roles" value={(jp.preferredRoles ?? []).join(', ') || '-'} full />
        <WorkspaceField label="Job type" value={jp.jobType || '-'} full />
      </SectionCard>

      {feedbackSection}
    </>
  );

  const candidate = (
    <SectionCard title="Candidate details" icon={<PersonOutlineIcon />}>
      <WorkspaceField label="Full name" value={pd.fullName} />
      <WorkspaceField label="Email" value={pd.email} mono />
      <WorkspaceField label="Phone" value={pd.phone} mono />
      <WorkspaceField
        label="Location"
        value={[pd.city, pd.state, pd.country].filter(Boolean).join(', ') || '-'}
      />
    </SectionCard>
  );

  const documents = (
    <SectionCard title="Documents" icon={<DescriptionOutlinedIcon />}>
      <WorkspaceField
        label="Resume"
        full
        value={
          app.jobSeeker?.resumeUrl ? (
            /*
              A button that signs, not a link to the stored URL.

              The stored resumeUrl is a public-form S3 URL written at upload time, and
              `trymintly-resume` is private: anything uploaded since the bucket tightened returns
              AccessDenied, which is what an HR saw here. Objects from before the change carry a
              world-readable ACL and still open, so this failed for newer candidates only -- which
              reads as "the page is broken" rather than as a link that was never going to work.

              resolveResumeUrl asks the BFF to presign it, the same route the job-seeker workspace
              uses. That is why the résumé opened there and not here.
            */
            <Button
              variant="text"
              size="small"
              disabled={resumeOpening}
              onClick={async () => {
                setResumeOpening(true);
                try {
                  const signed = await resolveResumeUrl(app.jobSeeker?.resumeUrl);
                  if (signed) window.open(signed, '_blank', 'noopener,noreferrer');
                  else showToast('Could not open this resume.', 'error');
                } finally {
                  setResumeOpening(false);
                }
              }}
            >
              {resumeOpening ? 'Opening…' : 'Open resume'}
            </Button>
          ) : (
            'No resume on file'
          )
        }
      />
    </SectionCard>
  );

  const history = (
      <>
      <SectionCard title="Status history" icon={<ArticleOutlinedIcon />}>
        {(app.history ?? []).length === 0 ? (
          <WorkspaceField label="History" value="No status changes recorded" full />
        ) : (
          <Box sx={{ gridColumn: '1 / -1', display: 'flex', flexDirection: 'column', gap: 1 }}>
            {(app.history ?? []).map((h, i) => (
              <WorkspaceField key={i} label={fmt(h.date)} value={`${h.status} · ${h.changedBy ? resolveAuthor(h.changedBy) : 'System'}`} full />
            ))}
          </Box>
        )}
      </SectionCard>
        <RecordActivityCard recordId={app.id} />
      </>
  );

  const tabContent: Record<string, React.ReactNode> = {
    overview,
    candidate,
    interviews: (
      <SectionCard title="Interviews" icon={<EventOutlinedIcon />}>
        <WorkspaceField label="Interviews" value="No interviews scheduled" full />
      </SectionCard>
    ),
    documents,
    history,
  };

  const composer = (
    <NotesSection
      isEditMode
      showVisibilityToggle
      notes={(app.notes ?? []).map((n, i) => ({
        id: String(i),
        note: n.note,
        changedBy: resolveAuthor(n.changedBy),
        date: n.date,
        visibility: n.visibility,
      }))}
      onAdd={addNote}
    />
  );

  return (
    <>
      <EntityWorkspace
        backLabel="Applications"
        onBack={() => navigate('/applications/list')}
        identityRail={identityRail}
        tabs={TABS}
        activeTabId={activeTab}
        onTabChange={setActiveTab}
        stepper={activeTab === 'overview' ? stageCard : undefined}
        activityRail={<ActivityRail composer={composer} timeline={timeline} />}
        activityOpen={activityOpen}
        onToggleActivity={toggleActivity}
      >
        {tabContent[activeTab]}
      </EntityWorkspace>

      {/* `confirmText` rather than a hand-built field: ConfirmDialog already does type-to-confirm,
          which is what this dialog was rebuilding around a FormField. */}
      <ConfirmDialog
        open={delOpen}
        title="Delete this application?"
        body="This permanently deletes the application and everything attached to it. The candidate and the job are not affected."
        confirmText={app.job?.title ?? ''}
        confirmLabel="Job title"
        onConfirm={doDelete}
        onCancel={() => setDelOpen(false)}
      />

      {/* Typed-back confirmation, because this one is not undoable in any useful sense: the
          employer sees the candidate the moment it succeeds, and the record says a named person
          decided they could. */}
      <ConfirmDialog
        open={overrideOpen}
        title="Submit without the candidate's confirmation?"
        body={
          <>
            {app.jobSeeker?.personalDetails?.fullName || 'This candidate'} has not confirmed by email that we may
            represent them for this role. Submitting anyway records your name and the time against
            the application, and makes the candidate visible to the employer immediately. Do this
            only when they have agreed somewhere else, such as on a call.
          </>
        }
        confirmText={app.jobSeeker?.personalDetails?.email ?? ''}
        confirmLabel="Candidate email"
        actionLabel="Submit anyway"
        busy={overriding}
        onConfirm={overrideConsent}
        onCancel={() => setOverrideOpen(false)}
      />
    </>
  );
};

export default ApplicationWorkspace;
